/* eslint-disable no-alert */
import React, { PureComponent } from 'react';
import { Helmet } from "react-helmet";
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { Checkbox, Select, Spin, Alert, Tag } from 'antd';
import EditSupplierHOC from './EditSupplierCapsHOC';
import NFButton from '../../components/primitives/NFButton';
import {
  SupplierProfileFieldsLeft,
  SupplierProfileFieldsRight,
  ClassificationsAndCapsFieldsLeft,
  ClassificationsAndCapsFieldsRight,
} from './formConfig';
import {
  // weightRange,
  Aluminum,
  copper,
  Iron,
  MMC,
  other,
  steel,
  alloySteel,
} from '../../config/SupplierSearchOptions/MaterialOptions';
import EditMinMax from './EditMinMax';
import { convertDimensionsToMinMaxDimensions, convertMinMaxDimensionsToDimensions } from '../../config/convert/convertDimensions';

const { Option } = Select;

// Custom tag render function to prevent tag text truncation
// eslint-disable-next-line react/prop-types
const tagRender = props => {
  // eslint-disable-next-line react/prop-types
  const { label, closable, onClose } = props;
  const onPreventMouseDown = event => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      color="orange"
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ whiteSpace: 'normal', height: 'auto', maxWidth: '100%', marginBottom: '4px' }}
    >
      {label}
    </Tag>
  );
};

class EditSupplierCaps extends PureComponent {
  static initialState = {
    isLoading: true,
  };
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onAlloyChange: PropTypes.func.isRequired,
    onMaterialChange: PropTypes.func.isRequired,
    setInitialValuesForAlloy: PropTypes.func.isRequired,

    // .shape({})
    initialValues: PropTypes.object,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
  };

  static defaultProps = {
    initialValues: {},
    isLoading: false,
    error: null,
  };

  createOptions = (value, showSelectedKeys = false) => {
    let obj;
    let materialValue;
    switch (value) {
      case 'Alloy Steel':
        obj = alloySteel;
        materialValue = 'alloySteel';
        break;
      case 'Aluminum':
        obj = Aluminum;
        materialValue = 'aluminum';
        break;
      case 'Copper':
        obj = copper;
        materialValue = 'copper';
        break;
      case 'Iron':
        obj = Iron;
        materialValue = 'iron';
        break;
      case 'Other Non-Ferrous':
        obj = other;
        materialValue = 'other';
        break;
      case 'Steel':
        obj = steel;
        materialValue = 'steel';
        break;
      case 'Metal Matrix Composites':
        obj = MMC;
        materialValue = 'mmc';
        break;
      default:
        return showSelectedKeys ? [] : [];
    }

    if (!obj) {
      return showSelectedKeys ? [] : [];
    }

    const objKeys = Object.values(obj);

    const options = objKeys.map(key => <Option key={key}>{key}</Option>);
    if (showSelectedKeys) {
      const array = this.props.initialValues.alloy.filter(alloy => obj[alloy]);
      this.props.setInitialValuesForAlloy(materialValue, array);
      return array;
    }
    return options;
  };

  renderMaterialField = (material, values, side) => {
    const hasAlloy = ['Copper', 'Iron', 'Aluminum', 'Steel', 'Other Non-Ferrous', 'Metal Matrix Composites'].includes(material);

    if (hasAlloy) {
      return (
        <div className="material-with-alloy">
          <Checkbox
            value={material}
            onChange={(e) => {
              const materials = e.target.checked
                ? [...(this.props.initialValues.material || []), material]
                : (this.props.initialValues.material || []).filter(m => m !== material);
              this.props.onMaterialChange(materials, side, values);
            }}
            checked={(this.props.initialValues.material || []).includes(material)}
          >
            {material}
          </Checkbox>
          <Select
            mode="multiple"
            placeholder="Click checkbox to select materials"
            onChange={alloy => this.props.onAlloyChange(alloy, material, values)}
            value={this.createOptions(material, true)}
            style={{ width: '100%' }}
            tagRender={tagRender}
          >
            {this.createOptions(material)}
          </Select>
        </div>
      );
    }

    return (
      <Checkbox
        value={material}
        onChange={(e) => {
          const materials = e.target.checked
            ? [...(this.props.initialValues.material || []), material]
            : (this.props.initialValues.material || []).filter(m => m !== material);
          this.props.onMaterialChange(materials, side, values);
        }}
        checked={(this.props.initialValues.material || []).includes(material)}
      >
        {material}
      </Checkbox>
    );
  };

  render() {
    const MaterialLeft = [
      'Alloy Steel',
      'Aluminum',
      'Brass',
      'Carbon Steel',
      'Cobalt',
      'Copper',
      'Copper (Brass/Bronze)',
      'High Temp Alloy',
      'Iron',
      'Lead',
      'Magnesium',
      'Micro Alloy',
    ];

    const MaterialRight = [
      'Metal Matrix Composites',
      'Nickel',
      'Other Materials As Specified',
      'Other Non-Ferrous',
      'Steel',
      'Stainless Steel',
      'Superalloys',
      'Titanium',
      'Tool Steel',
      'Unspecified',
      'Zinc',
    ];

    const { onSubmit, initialValues, onMaterialChange, isLoading, error } = this.props;

    if (isLoading) {
      return (
        <div className="edit-supplier-caps">
          <div className="edit-supplier-caps__header">
            <h1>Supplier Capabilities Management</h1>
            <p>Loading your capabilities data...</p>
          </div>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '20px', color: '#666' }}>Loading supplier capabilities...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="edit-supplier-caps">
        <Helmet>
          <meta charSet="utf-8" />
          <title>NFFS ICON - Edit Supplier Capabilities Page</title>
        </Helmet>

        <div className="edit-supplier-caps__header">
          <h1>Supplier Capabilities Management</h1>
          <p>Update your company&apos;s manufacturing capabilities and material specifications</p>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          onSubmit={onSubmit}
          initialValues={initialValues}
          render={({
            handleSubmit,
            values,
            submitting,
            //  pristine, invalid, submitting
          }) => {
            console.log(initialValues)
            return (
              <form className="nf-new-org-form" onSubmit={handleSubmit}>
                {/* Supplier Profile Information Section */}
                <div className="edit-supplier-caps__section">
                  <div className="edit-supplier-caps__section-title">
                    Supplier Profile Information
                  </div>
                  <div className="edit-supplier-caps__form-grid">
                    <div>
                      {SupplierProfileFieldsLeft.map(fieldConfig => (
                        <div key={fieldConfig.name} className="edit-supplier-caps__field-group">
                          <Field {...fieldConfig} />
                        </div>
                      ))}
                    </div>
                    <div>
                      {SupplierProfileFieldsRight.map(fieldConfig => (
                        <div key={fieldConfig.name} className="edit-supplier-caps__field-group">
                          <Field {...fieldConfig} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Supplier Capabilities Section */}
                <div className="edit-supplier-caps__section">
                  <div className="edit-supplier-caps__section-title">
                    Supplier Capabilities
                  </div>
                  <div className="edit-supplier-caps__form-grid">
                    <div>
                      {ClassificationsAndCapsFieldsLeft.map(fieldConfig => (
                        <div key={fieldConfig.name} className="edit-supplier-caps__field-group">
                          <Field {...fieldConfig} />
                        </div>
                      ))}
                    </div>
                    <div>
                      {ClassificationsAndCapsFieldsRight.map(fieldConfig => {
                        console.log(fieldConfig)
                        return (
                          <div key={fieldConfig.name} className="edit-supplier-caps__field-group">
                            <Field {...fieldConfig} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Materials Section */}
                <div className="edit-supplier-caps__section">
                  <div className="edit-supplier-caps__section-title">
                    Materials
                  </div>
                  <div className="edit-supplier-caps__materials-section">
                    <div className="edit-supplier-caps__form-grid">
                      <Checkbox.Group
                        value={initialValues.material}
                        onChange={materials => onMaterialChange(materials, 'all', values)}
                      >
                        <div className="materials-grid">
                          {[...MaterialLeft, ...MaterialRight].map(material => (
                            <div key={material} className="material-cell">
                              {this.renderMaterialField(material, values, MaterialLeft.includes(material) ? 'left' : 'right')}
                            </div>
                          ))}
                        </div>
                      </Checkbox.Group>
                    </div>
                  </div>
                </div>

                {/* Min/Max Section */}
                <div className="edit-supplier-caps__section">
                  <div className="edit-supplier-caps__section-title">
                    Min/Max
                  </div>
                  <div className="edit-supplier-caps__materials-section">
                    <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                      <strong>Instructions:</strong> 1) Click &quot;Add Material&quot; and select a material (e.g., &quot;Alloy&quot;), 2) Click on the material panel to expand it, 3) Select a dimension (e.g., &quot;Min&quot; or &quot;Max&quot;), 4) Enter a value (e.g., &quot;3&quot;), 5) Click outside the input to save.
                    </div>
                    <div className="edit-supplier-caps__form-grid">
                      <Field name="minMaxDimensions">
                        {({ input, meta }) => {
                          console.log('Field - input.value:', input.value);
                          console.log('Field - meta:', meta);
                          return (
                            <div>
                              <EditMinMax
                                dimensions={convertMinMaxDimensionsToDimensions(input.value)}
                                setDimensions={(dimensions) => {
                                  console.log('Field - setDimensions called with:', dimensions);
                                  const minMaxDimensions = convertDimensionsToMinMaxDimensions(dimensions);
                                  console.log('Field - calling input.onChange with:', minMaxDimensions);
                                  input.onChange(minMaxDimensions);
                                }}
                              />
                              {meta.touched && meta.error && <span className="error">{meta.error}</span>}
                            </div>
                          );
                        }}
                      </Field>
                    </div>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="edit-supplier-caps__submit-section">
                  <NFButton
                    className="nf-button--form nf-button--orange"
                    type="submit"
                    disabled={submitting}
                  // disabled={pristine || submitting || invalid}
                  >
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </NFButton>
                </div>
              </form>
            );
          }}
        />
      </div>
    );
  }
  static outDimensions = dim => {
    console.log("Dimensions:", dim)
  }

}

export default EditSupplierHOC(EditSupplierCaps);


















// WEBPACK FOOTER //
// ./src/components/EditSupplierCaps/EditSupplierCaps.jsx